import { useCallback, useRef, useState, useEffect, ReactElement } from 'react';
import QrScanner from 'qr-scanner';
import CryptoJS from 'crypto-js';
import { ToastType, useTosterStore } from '@store/toster';
import '@assets/styles/pages/qr-code-scanner.scss';
import { DecodedQRData, DecryptionConfig, IVisitCompleteQrProps, QR_TIME_TOLERANCE_MS, resolveQrStoreKey } from '.';
import { useQrVerificationStore } from '@store/qrVerificationStore';

const DECRYPTION_CONFIG: DecryptionConfig = {
	algorithm: process.env.REACT_APP_ENCRYPTION_ALGORITHM,
	key: process.env.REACT_APP_ENCRYPTION_KEY,
	iv: process.env.REACT_APP_ENCRYPTION_IV,
} as const;

export const VisitCompleteQr = ({ siteDetails, verificationKey }: IVisitCompleteQrProps): ReactElement => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const scannerRef = useRef<QrScanner | null>(null);

	const { setToaster } = useTosterStore();
	const [isScanning, setIsScanning] = useState<boolean>(false);
	const [cameraReady, setCameraReady] = useState<boolean>(false);
	const storeKey = resolveQrStoreKey(verificationKey);

	const isKeyVerified = useQrVerificationStore((s) => s.isVerified(storeKey));
	const setVerified = useQrVerificationStore((s) => s.setVerified);

	/**
	 * Decrypts QR code data and validates it
	 */
	const decryptQRCode = useCallback(
		(encryptedData: string): DecodedQRData => {
			const result: DecodedQRData = {
				originalData: encryptedData,
				membershipNumber: null,
				timestamp: null,
				decryptedData: null,
				isValid: false,
			};

			const raw = encryptedData?.trim();
			if (!raw) return result;

			try {
				const base64Data = decodeURIComponent(raw);
				const key = CryptoJS.enc.Utf8.parse(DECRYPTION_CONFIG.key ?? '');
				const iv = CryptoJS.enc.Utf8.parse(DECRYPTION_CONFIG.iv ?? '');

				const decrypted = CryptoJS.AES.decrypt(base64Data, key, {
					iv,
					mode: CryptoJS.mode.CBC,
					padding: CryptoJS.pad.Pkcs7,
				});

				const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
				if (!decryptedString) throw new Error('Failed to decrypt QR code');

				result.decryptedData = decryptedString;

				const [membershipNumber, timestamp] = decryptedString.split('_');
				result.membershipNumber = membershipNumber ?? null;
				result.timestamp = timestamp ?? null;

				const currentTime = Date.now();
				const qrTime = parseInt(timestamp, 10);
				const isTimeValid = Number.isFinite(qrTime) && Math.abs(currentTime - qrTime) <= QR_TIME_TOLERANCE_MS;
				const isMembershipValid = Boolean(siteDetails?.membershipno) && siteDetails?.membershipno === membershipNumber;

				result.isValid = isTimeValid && isMembershipValid;
			} catch (error) {
				return result;
			}
			return result;
		},
		[siteDetails?.membershipno]
	);

	/**
	 * Cleans up scanner and video stream
	 */
	const cleanupScanner = useCallback((): void => {
		if (scannerRef.current) {
			scannerRef.current.stop();
			scannerRef.current.destroy();
			scannerRef.current = null;
		}
		const video = videoRef.current;
		if (video?.srcObject) {
			const mediaStream = video.srcObject as MediaStream;
			mediaStream.getTracks().forEach((t) => t.stop());
			video.srcObject = null;
		}
		setCameraReady(false);
	}, []);

	/**
	 * Sets up the QR scanner
	 */
	const handleQRScanResult = useCallback(
		(scanResult: QrScanner.ScanResult) => {
			if (isScanning) return;
			setIsScanning(true);

			try {
				const decoded = decryptQRCode(scanResult.data);
				if (decoded?.isValid) {
					setVerified(storeKey, true);
					setToaster(true, ToastType.success, 'Site Visit Verified Successfully.');
					cleanupScanner();
				} else {
					setToaster(true, ToastType.error, 'Invalid QR. Please scan again.');
				}
			} finally {
				setIsScanning(false);
			}
		},
		[decryptQRCode, cleanupScanner, isScanning, setToaster, setVerified, storeKey]
	);

	const setupQrScanner = useCallback(async (): Promise<void> => {
		if (!videoRef.current) return;
		try {
			if (scannerRef.current) {
				cleanupScanner();
				await new Promise((r) => setTimeout(r, 100));
			}
			scannerRef.current = new QrScanner(videoRef.current, handleQRScanResult, {
				highlightScanRegion: true,
				maxScansPerSecond: 1,
				highlightCodeOutline: true,
				returnDetailedScanResult: true,
				preferredCamera: 'environment',
			});
			await scannerRef.current.start();
			setCameraReady(true);
		} catch {
			setToaster(true, ToastType.error, 'Unable to start camera. Check permissions and try again.');
			setCameraReady(false);
		}
	}, [cleanupScanner, handleQRScanResult, setToaster]);

	useEffect(() => {
		if (!cameraReady && !isKeyVerified) {
			const timer = setTimeout(() => {
				setupQrScanner();
			}, 300);
			return (): void => clearTimeout(timer);
		}
	}, [cameraReady, setupQrScanner, isKeyVerified]);

	useEffect((): (() => void) => () => cleanupScanner(), [cleanupScanner]);

	return (
		<>
			{!isKeyVerified && (
				<section className="qr-scanner-section">
					<div className="container">
						<div className="video-wrapper">
							<video ref={videoRef} style={{ width: '100%' }} autoPlay muted playsInline aria-label="QR code scanner" />
						</div>
					</div>
				</section>
			)}
			{isKeyVerified && <h2>Site Visit Verified Successfully</h2>}
		</>
	);
};
